import React from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  STANDARD_METRIC_FIELDS,
  BOOLEAN_VALUES,
  NO_VALUE_OPERATORS,
  getMetricResultType,
  getOperators,
} from "../../constants/metricConditions";

const SUB_PROPERTIES = [
  { value: "metric_value", label: "Value" },
  { value: "outcome_classification", label: "Outcome Classification" },
];

// Standard result-classification vocabulary (mirrors the metric editor). Used as a
// fallback when a criterion defines no classifications of its own.
const CLASSIFICATION_OPTIONS = [
  "meets_expectations",
  "exceeds_expectations",
  "requires_attention",
  "goal_achieved",
  "goal_missed",
];

export default function ConditionRow({ condition, index, metrics, onChange, onRemove }) {
  // Resolve by id first. Falling back to metric_key only when there's no id, because
  // custom metrics all have a null standard_metric_key — matching on null would wrongly
  // grab the first custom metric in the list.
  const selectedMetric =
    condition.metric_id != null
      ? metrics.find((m) => m.id === condition.metric_id)
      : (condition.metric_key
          ? metrics.find((m) => m.standard_metric_key === condition.metric_key)
          : undefined);

  const isStandard = selectedMetric?.standard_metric_key && STANDARD_METRIC_FIELDS[selectedMetric.standard_metric_key];

  // Sub-criteria + result classifications are pulled from the SELECTED metric's defined
  // structured output (the metrics table). For each rule:
  //   set_metrics_name          → a sub-criterion (e.g. "user_greeting")
  //   as_metrics_expected_value → a value that criterion can take (e.g. "pass"/"fail")
  //   outcome_classification    → a result classification for that criterion
  // A metric supports sub-conditions only when it defines structured output. A condition
  // can hold MANY sub-criteria (condition.sub_criteria[]) — all are ANDed.
  const definedRules = Array.isArray(selectedMetric?.structured_output) ? selectedMetric.structured_output : [];
  const subCriteriaOptions = [...new Set(definedRules.map((r) => r.set_metrics_name).filter(Boolean))];
  const supportsSubCondition = subCriteriaOptions.length > 0;
  const subCriteria = Array.isArray(condition.sub_criteria) ? condition.sub_criteria : [];
  const subActive = subCriteria.length > 0;

  // Value options for one sub-criterion row, scoped to the criterion + chosen property.
  // metric_value → that criterion's defined expected values; outcome_classification →
  // that criterion's defined classifications, falling back to the standard vocabulary.
  const valueOptionsFor = (subMetric, subProperty) => {
    const rules = definedRules.filter((r) => r.set_metrics_name === subMetric);
    const raw = subProperty === "outcome_classification"
      ? rules.map((r) => r.outcome_classification)
      : rules.map((r) => r.as_metrics_expected_value);
    const cleaned = [...new Set(raw.filter((v) => v !== null && v !== undefined && v !== ""))].map(String);
    if (subProperty === "outcome_classification" && cleaned.length === 0) return CLASSIFICATION_OPTIONS;
    return cleaned;
  };

  const resultType = getMetricResultType(selectedMetric);
  const operators = getOperators(selectedMetric);
  const needsValue = !NO_VALUE_OPERATORS.includes(condition.operator);
  const enumValues = selectedMetric?.result_type_config?.values || [];

  // ── sub-criteria mutators ──
  const newSubRow = () => ({ sub_metric: subCriteriaOptions[0] || "", sub_property: "metric_value", value: "" });
  const setSubCriteria = (next) => onChange(index, { sub_criteria: next, operator: "equals" });
  const addSubRow = () => setSubCriteria([...subCriteria, newSubRow()]);
  const updateSubRow = (ri, patch) => setSubCriteria(subCriteria.map((r, i) => (i === ri ? { ...r, ...patch } : r)));
  const removeSubRow = (ri) => onChange(index, { sub_criteria: subCriteria.filter((_, i) => i !== ri) });

  const handleMetricChange = (e) => {
    const metricId = parseInt(e.target.value);
    const metric = metrics.find((m) => m.id === metricId);
    if (!metric) return;

    const update = {
      metric_id: metric.id,
      metric_key: metric.standard_metric_key || null,
      field: null,
      sub_criteria: [],
      operator: "equals",
      value: "",
    };
    if (metric.standard_metric_key && STANDARD_METRIC_FIELDS[metric.standard_metric_key]) {
      update.field = STANDARD_METRIC_FIELDS[metric.standard_metric_key][0]?.value;
    }
    onChange(index, update);
  };

  // Reserve right padding for the native dropdown arrow (pr-8) and a min width so
  // short labels like "=" don't render underneath the chevron.
  const selectCls = "text-sm border border-input rounded-md pl-2.5 pr-8 py-1.5 bg-card min-w-[5rem]";
  const subSelectCls = "text-xs border border-border rounded-md pl-2 pr-7 py-1 bg-card";

  return (
    <div className="bg-muted/50 rounded-md p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
          {/* Metric selector */}
          <select
            value={condition.metric_id || ""}
            onChange={handleMetricChange}
            className={selectCls}
          >
            <option value="">Select metric...</option>
            <optgroup label="Standard Metrics">
              {metrics.filter((m) => !m.is_custom && m.analysis_type === "audio").map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </optgroup>
            <optgroup label="LLM Metrics (System)">
              {metrics.filter((m) => !m.is_custom && m.analysis_type !== "audio").map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </optgroup>
            <optgroup label="Custom Metrics">
              {metrics.filter((m) => m.is_custom).map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </optgroup>
          </select>

          {/* Field selector for standard audio metrics */}
          {isStandard && (
            <select
              value={condition.field || ""}
              onChange={(e) => onChange(index, { field: e.target.value })}
              className={selectCls}
            >
              {STANDARD_METRIC_FIELDS[selectedMetric.standard_metric_key].map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          )}

          {/* Operator + value — for the parent metric. Hidden when sub-criteria are
              active, because the sub-criteria section below defines the comparison. */}
          {!subActive && (
            <>
              <select
                value={condition.operator || "equals"}
                onChange={(e) => onChange(index, { operator: e.target.value, ...(NO_VALUE_OPERATORS.includes(e.target.value) ? { value: "" } : {}) })}
                className={selectCls}
              >
                {operators.map((op) => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>

              {!needsValue ? (
                <div className="text-xs text-muted-foreground flex items-center px-2">(no value needed)</div>
              ) : resultType === "boolean" ? (
                <select
                  value={condition.value || ""}
                  onChange={(e) => onChange(index, { value: e.target.value })}
                  className={selectCls}
                >
                  <option value="">Select...</option>
                  {BOOLEAN_VALUES.map((v) => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
              ) : resultType === "rating" ? (
                <select
                  value={condition.value ?? ""}
                  onChange={(e) => onChange(index, { value: parseInt(e.target.value) })}
                  className={selectCls}
                >
                  <option value="">Select...</option>
                  {[1, 2, 3, 4, 5].map((v) => (
                    <option key={v} value={v}>{v} / 5</option>
                  ))}
                </select>
              ) : resultType === "enum" && enumValues.length > 0 ? (
                <select
                  value={condition.value || ""}
                  onChange={(e) => onChange(index, { value: e.target.value })}
                  className={selectCls}
                >
                  <option value="">Select...</option>
                  {enumValues.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={resultType === "standard" || resultType === "numeric" ? "number" : "text"}
                  value={condition.value ?? ""}
                  onChange={(e) => onChange(index, { value: e.target.value })}
                  placeholder="Value"
                  className="w-40 text-sm border border-input rounded-md px-2.5 py-1.5 bg-card"
                />
              )}
            </>
          )}
        {onRemove && <button onClick={() => onRemove(index)} className="text-red-400 hover:text-red-600" title="Remove condition">
          <Trash2 className="h-4 w-4" />
        </button>}
      </div>

      {/* Sub-criteria (optional) — one or more checks against the metric's DEFINED
          sub-criteria. Each row: criterion → Value | Outcome Classification → value,
          all sourced from the metric's structured-output definition; all ANDed. */}
      {supportsSubCondition && !subActive && (
        <button
          onClick={addSubRow}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground ml-1"
        >
          <Plus className="h-3 w-3" /> Add sub-criteria
        </button>
      )}
      {supportsSubCondition && subActive && (
        <div className="pl-4 border-l-2 border-border ml-1 space-y-1.5">
          {subCriteria.map((row, ri) => {
            const valueOptions = valueOptionsFor(row.sub_metric, row.sub_property || "metric_value");
            return (
              <div key={ri} className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground shrink-0 w-20">Sub-criterion:</span>
                <select
                  value={row.sub_metric || ""}
                  onChange={(e) => updateSubRow(ri, { sub_metric: e.target.value, value: "" })}
                  className={subSelectCls}
                >
                  <option value="">Select criterion…</option>
                  {subCriteriaOptions.map((name) => (
                    <option key={name} value={name}>{name.replace(/_/g, " ")}</option>
                  ))}
                </select>
                {row.sub_metric && (
                  <>
                    <select
                      value={row.sub_property || "metric_value"}
                      onChange={(e) => updateSubRow(ri, { sub_property: e.target.value, value: "" })}
                      className={subSelectCls}
                    >
                      {SUB_PROPERTIES.map((sp) => (
                        <option key={sp.value} value={sp.value}>{sp.label}</option>
                      ))}
                    </select>
                    <span className="text-xs text-muted-foreground">=</span>
                    {valueOptions.length > 0 ? (
                      <select
                        value={row.value ?? ""}
                        onChange={(e) => updateSubRow(ri, { value: e.target.value })}
                        className={subSelectCls}
                      >
                        <option value="">Select…</option>
                        {valueOptions.map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={row.value ?? ""}
                        onChange={(e) => updateSubRow(ri, { value: e.target.value })}
                        placeholder="value"
                        className="text-xs border border-border rounded-md px-2 py-1 bg-card w-32"
                      />
                    )}
                  </>
                )}
                <button
                  onClick={() => removeSubRow(ri)}
                  className="text-gray-300 hover:text-red-500"
                  title="Remove this sub-criteria"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
          <button
            onClick={addSubRow}
            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 ml-20"
          >
            <Plus className="h-3 w-3" /> Add another sub-criteria
          </button>
        </div>
      )}
    </div>
  );
}
